import { type QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

const DEFAULT_STALE_TIME_MS = 60_000;

export interface PageResult<T> {
  items: T[];
  total: number;
}

/** Continues from the loaded count, which stays right after local deletes. */
export function getNextPageOffset<T>(
  lastPage: PageResult<T>,
  allPages: PageResult<T>[]
): number | undefined {
  const loaded = allPages.reduce((count, page) => count + page.items.length, 0);
  return lastPage.items.length > 0 && loaded < lastPage.total
    ? loaded
    : undefined;
}

/** Offset pages can overlap when rows are added between page loads. */
export function flattenPages<T extends { id: number }>(
  pages: PageResult<T>[] = []
): T[] {
  const seen = new Set<number>();
  return pages
    .flatMap((page) => page.items)
    .filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
}

type KeepPrevious<Q extends object> = (previousQuery: Q | undefined) => boolean;

interface UseInfinitePageOptions<T extends { id: number }, Q extends object> {
  queryKey: QueryKey;
  query: Q;
  pageSize: number;
  fetchPage: (
    params: Q & { offset: number; limit: number }
  ) => Promise<PageResult<T>>;
  enabled?: boolean;
  staleTime?: number;
  /**
   * Keep the previous page visible while the next query loads. A function
   * can refuse, for example when the catalog itself changed.
   */
  keepPrevious?: boolean | KeepPrevious<Q>;
}

export function useInfinitePage<T extends { id: number }, Q extends object>({
  queryKey,
  query,
  pageSize,
  fetchPage,
  enabled = true,
  staleTime = DEFAULT_STALE_TIME_MS,
  keepPrevious = true,
}: UseInfinitePageOptions<T, Q>) {
  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchPage({ ...query, offset: pageParam, limit: pageSize }),
    initialPageParam: 0,
    getNextPageParam: getNextPageOffset,
    enabled,
    staleTime,
    placeholderData: (previous, previousQuery) => {
      if (!previous || keepPrevious === false) {
        return undefined;
      }
      if (keepPrevious === true) {
        return previous;
      }
      const previousParams =
        previousQuery?.queryKey[previousQuery.queryKey.length - 1];
      const previousQueryParams =
        previousParams !== null && typeof previousParams === 'object'
          ? (previousParams as Q)
          : undefined;
      return keepPrevious(previousQueryParams) ? previous : undefined;
    },
  });

  const items = useMemo(() => flattenPages(data?.pages), [data]);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    items,
    isLoading,
    isFetching,
    loadMore,
  };
}
